import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Dashboard Page Component
 *
 * Main admin dashboard with real data from Analytics API
 *
 * @package WooAIChatbot
 */
import { useState, useEffect, useCallback } from 'react';
import { motion } from 'motion/react';
import { MessageSquare, ShoppingCart, Eye, Percent, TrendingUp, TrendingDown, RefreshCw, Clock, Package, User, Users, Zap, X, DollarSign, Calculator, } from 'lucide-react';
import { useAnalytics } from '../../contexts/AnalyticsContext';
import { useTranslation } from '../../hooks/useTranslation';
import { HourlyUsageChart } from '../../components/analytics/HourlyUsageChart';
// Modal component
const Modal = ({ isOpen, onClose, title, children, }) => {
    if (!isOpen)
        return null;
    return (_jsx("div", { style: {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100000,
        }, onClick: onClose, children: _jsxs("div", { style: {
                backgroundColor: '#ffffff',
                borderRadius: '16px',
                width: '90%',
                maxWidth: '900px',
                maxHeight: '90vh',
                overflow: 'hidden',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            }, onClick: (e) => e.stopPropagation(), children: [_jsxs("div", { style: {
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '20px 24px',
                        borderBottom: '1px solid #e5e7eb',
                    }, children: [_jsx("h2", { style: { fontSize: '20px', fontWeight: 600, color: '#111827', margin: 0 }, children: title }), _jsx("button", { onClick: onClose, style: {
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                padding: '8px',
                                borderRadius: '8px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }, children: _jsx(X, { style: { width: '20px', height: '20px', color: '#6b7280' } }) })] }), _jsx("div", { style: { padding: '24px', maxHeight: 'calc(90vh - 80px)', overflowY: 'auto' }, children: children })] }) }));
};
// Styles
const styles = {
    page: {
        padding: '32px',
        backgroundColor: '#f9fafb',
        minHeight: '100%',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen-Sans, Ubuntu, Cantarell, "Helvetica Neue", sans-serif',
        lineHeight: '1.5',
        boxSizing: 'border-box',
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: '24px',
    },
    headerContent: {
        flex: 1,
    },
    headerTitle: {
        fontSize: '24px',
        fontWeight: 600,
        color: '#111827',
        marginBottom: '4px',
    },
    headerSubtitle: {
        fontSize: '14px',
        color: '#6b7280',
    },
    refreshButton: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '8px 16px',
        borderRadius: '10px',
        border: '1px solid #d1d5db',
        backgroundColor: '#ffffff',
        color: '#374151',
        fontSize: '14px',
        fontWeight: 500,
        cursor: 'pointer',
    },
    headerActions: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
    },
    periodSelector: {
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        padding: '4px',
        backgroundColor: '#f3f4f6',
        borderRadius: '10px',
    },
    periodButton: {
        padding: '6px 12px',
        borderRadius: '8px',
        border: 'none',
        backgroundColor: 'transparent',
        color: '#6b7280',
        fontSize: '14px',
        fontWeight: 500,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
    },
    periodButtonActive: {
        padding: '6px 12px',
        borderRadius: '8px',
        border: 'none',
        backgroundColor: '#ffffff',
        color: '#111827',
        fontSize: '14px',
        fontWeight: 500,
        cursor: 'pointer',
        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
        transition: 'all 0.2s ease',
    },
    cardsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '24px',
        marginBottom: '24px',
    },
    card: {
        backgroundColor: '#ffffff',
        borderRadius: '16px',
        border: '1px solid #e5e7eb',
        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
        padding: '20px',
    },
    cardIconBox: {
        width: '48px',
        height: '48px',
        borderRadius: '12px',
        backgroundColor: '#eff6ff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: '16px',
    },
    cardIcon: {
        width: '24px',
        height: '24px',
        color: '#6366f1',
    },
    cardLabel: {
        fontSize: '14px',
        color: '#6b7280',
        marginBottom: '4px',
    },
    cardValue: {
        fontSize: '32px',
        fontWeight: 700,
        color: '#111827',
        marginBottom: '8px',
    },
    cardTrend: {
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        fontSize: '14px',
    },
    trendUp: {
        color: '#10b981',
    },
    trendDown: {
        color: '#ef4444',
    },
    trendText: {
        color: '#9ca3af',
    },
    chartCard: {
        backgroundColor: '#ffffff',
        borderRadius: '16px',
        border: '1px solid #e5e7eb',
        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
        padding: '24px',
        marginBottom: '24px',
    },
    chartHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px',
    },
    chartTitle: {
        fontSize: '20px',
        fontWeight: 600,
        color: '#111827',
    },
    chartLink: {
        fontSize: '14px',
        fontWeight: 500,
        color: '#6366f1',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
    },
    chartContainer: {
        height: '280px',
        display: 'flex',
        alignItems: 'flex-end',
        gap: '8px',
        padding: '20px 0',
    },
    chartBar: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '8px',
    },
    chartBarFill: {
        width: '100%',
        borderRadius: '4px 4px 0 0',
        backgroundColor: '#6366f1',
        minHeight: '4px',
        transition: 'height 0.3s ease',
    },
    chartBarLabel: {
        fontSize: '11px',
        color: '#6b7280',
    },
    bottomGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: '24px',
    },
    productsCard: {
        backgroundColor: '#ffffff',
        borderRadius: '16px',
        border: '1px solid #e5e7eb',
        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
        padding: '24px',
    },
    productsHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px',
    },
    productsTitle: {
        fontSize: '20px',
        fontWeight: 600,
        color: '#111827',
    },
    productsLink: {
        fontSize: '14px',
        fontWeight: 500,
        color: '#6366f1',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
    },
    productItem: {
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        padding: '16px 0',
        borderBottom: '1px solid #f3f4f6',
    },
    productItemLast: {
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        padding: '16px 0',
        borderBottom: 'none',
    },
    productNumber: {
        fontSize: '18px',
        fontWeight: 700,
        color: '#d1d5db',
        width: '24px',
    },
    productName: {
        fontSize: '14px',
        fontWeight: 500,
        color: '#111827',
    },
    productViews: {
        fontSize: '14px',
        color: '#6b7280',
    },
    activityCard: {
        backgroundColor: '#ffffff',
        borderRadius: '16px',
        border: '1px solid #e5e7eb',
        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
        padding: '24px',
    },
    activityTitle: {
        fontSize: '20px',
        fontWeight: 600,
        color: '#111827',
        marginBottom: '24px',
    },
    activityItem: {
        display: 'flex',
        gap: '12px',
        marginBottom: '20px',
    },
    activityIconBox: {
        width: '32px',
        height: '32px',
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    activityContent: {
        flex: 1,
    },
    activityItemTitle: {
        fontSize: '14px',
        fontWeight: 500,
        color: '#111827',
    },
    activityItemSubtitle: {
        fontSize: '14px',
        color: '#6b7280',
    },
    activityItemTime: {
        fontSize: '12px',
        color: '#9ca3af',
        marginTop: '2px',
    },
    loadingState: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px',
        color: '#6b7280',
    },
    emptyState: {
        textAlign: 'center',
        padding: '40px',
        color: '#9ca3af',
    },
    spinner: {
        animation: 'spin 1s linear infinite',
    },
};
// Helper to format numbers
const formatNumber = (num) => {
    if (num === undefined || num === null)
        return '0';
    if (num >= 1000000)
        return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000)
        return (num / 1000).toFixed(1) + 'K';
    return num.toLocaleString();
};
// Helper to format percentage
const formatPercent = (num) => {
    if (num === undefined || num === null)
        return '0%';
    return num.toFixed(1) + '%';
};
// Helper to format currency
const formatCurrency = (num, currency = '₪') => {
    if (num === undefined || num === null)
        return `0 ${currency}`;
    return `${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;
};
// Helper to format time ago
const formatTimeAgo = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    if (diffMins < 1)
        return 'Just now';
    if (diffMins < 60)
        return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24)
        return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
};
// Activity icon mapping
const getActivityIcon = (eventType) => {
    switch (eventType) {
        case 'conversation_start':
        case 'session_start':
            return { icon: MessageSquare, color: '#6366f1', bg: '#eff6ff' };
        case 'product_view':
        case 'product_click':
            return { icon: Eye, color: '#10b981', bg: '#ecfdf5' };
        case 'add_to_cart':
            return { icon: ShoppingCart, color: '#f59e0b', bg: '#fffbeb' };
        case 'playbook_started':
        case 'playbook_completed':
            return { icon: Zap, color: '#8b5cf6', bg: '#f5f3ff' };
        case 'message_sent':
        case 'message_received':
            return { icon: MessageSquare, color: '#3b82f6', bg: '#eff6ff' };
        default:
            return { icon: Clock, color: '#6b7280', bg: '#f3f4f6' };
    }
};
// Activity title mapping
const getActivityTitle = (event) => {
    const data = event.event_data || {};
    switch (event.event_type) {
        case 'conversation_start':
        case 'session_start':
            return { title: 'New conversation started' };
        case 'message_sent':
            return { title: 'User sent a message', subtitle: String(data.message || '').slice(0, 50) };
        case 'product_view':
            return { title: 'Product viewed', subtitle: String(data.product_name || data.product_id || '') };
        case 'product_click':
            return { title: 'Product clicked', subtitle: String(data.product_name || data.product_id || '') };
        case 'add_to_cart':
            return { title: 'Product added to cart', subtitle: String(data.product_name || data.product_id || '') };
        case 'playbook_started':
            return { title: 'Playbook started', subtitle: String(data.playbook_name || '') };
        case 'playbook_completed':
            return { title: 'Playbook completed', subtitle: String(data.playbook_name || '') };
        case 'topic_click':
            return { title: 'Topic selected', subtitle: String(data.topic_name || '') };
        default:
            return { title: event.event_type.replace(/_/g, ' ') };
    }
};
const getDateRangeForPeriod = (period) => {
    const to = new Date();
    const from = new Date();
    switch (period) {
        case 'day':
            from.setDate(from.getDate() - 1);
            break;
        case 'week':
            from.setDate(from.getDate() - 7);
            break;
        case 'month':
            from.setDate(from.getDate() - 30);
            break;
        case '90days':
            from.setDate(from.getDate() - 90);
            break;
    }
    return { from, to };
};
export function Dashboard() {
    const { t } = useTranslation();
    const { metrics, loading, refresh } = useAnalytics();
    const [recentEvents, setRecentEvents] = useState([]);
    const [eventsLoading, setEventsLoading] = useState(true);
    const [showChartModal, setShowChartModal] = useState(false);
    const [showProductsModal, setShowProductsModal] = useState(false);
    const [selectedPeriod, setSelectedPeriod] = useState('week');
    // Fetch recent events
    const fetchRecentEvents = useCallback(async () => {
        setEventsLoading(true);
        try {
            const globals = window.wooAiChatbot;
            const restUrl = globals?.restUrl || '/wp-json';
            const nonce = globals?.nonce || '';
            const response = await fetch(`${restUrl}/woo-ai/v1/analytics/events?limit=10`, {
                headers: {
                    'X-WP-Nonce': nonce,
                },
            });
            if (response.ok) {
                const data = await response.json();
                setRecentEvents(data.events || []);
            }
        }
        catch {
            // Silently fail - recent events are optional
        }
        finally {
            setEventsLoading(false);
        }
    }, []);
    useEffect(() => {
        fetchRecentEvents();
    }, [fetchRecentEvents]);
    // Handle refresh
    const handleRefresh = useCallback(() => {
        const dateRange = getDateRangeForPeriod(selectedPeriod);
        refresh(dateRange);
        fetchRecentEvents();
    }, [refresh, fetchRecentEvents, selectedPeriod]);
    // Handle period change
    const handlePeriodChange = useCallback((period) => {
        setSelectedPeriod(period);
        const dateRange = getDateRangeForPeriod(period);
        refresh(dateRange);
    }, [refresh]);
    // Extract data
    const summary = metrics?.summary;
    const hourly = metrics?.hourly || [];
    const topProducts = metrics?.topProducts || [];
    const comparison = metrics?.comparison;
    // Calculate trends
    const sessionsTrend = comparison?.changes?.sessions || 0;
    const messagesTrend = comparison?.changes?.messages || 0;
    const conversionsTrend = comparison?.changes?.conversions || 0;
    // KPI data from real metrics
    const kpiData = [
        {
            icon: MessageSquare,
            labelKey: 'totalConversations',
            value: formatNumber(summary?.totalSessions),
            trend: Math.abs(sessionsTrend),
            trendUp: sessionsTrend >= 0,
        },
        {
            icon: Eye,
            labelKey: 'totalMessages',
            value: formatNumber(summary?.totalMessages),
            trend: Math.abs(messagesTrend),
            trendUp: messagesTrend >= 0,
        },
        {
            icon: ShoppingCart,
            labelKey: 'productClickRate',
            value: formatPercent(summary?.productClickRate),
            trend: 0,
            trendUp: true,
        },
        {
            icon: Percent,
            labelKey: 'conversionRate',
            value: formatPercent(summary?.conversionRate),
            trend: Math.abs(conversionsTrend),
            trendUp: conversionsTrend >= 0,
        },
        {
            icon: TrendingUp,
            label: summary?.revenueLabel || 'Cart Value (via Chat)',
            value: `${(summary?.chatRevenue || 0).toLocaleString()} ₪`,
            trend: Math.abs(conversionsTrend),
            trendUp: conversionsTrend >= 0,
        },
        {
            icon: ShoppingCart,
            label: '🛒 Assisted Sales',
            value: formatNumber(summary?.totalAddToCart || summary?.conversions || 0),
            trend: Math.abs(conversionsTrend),
            trendUp: conversionsTrend >= 0,
        },
        {
            icon: Zap,
            label: '⚡ Engagement Rate',
            value: summary?.totalMessages && summary?.totalSessions
                ? `${(summary.totalMessages / summary.totalSessions).toFixed(1)} msg/chat`
                : '0',
            trend: Math.abs(messagesTrend),
            trendUp: messagesTrend >= 0,
        },
        {
            icon: DollarSign,
            label: 'Revenue/Chat',
            value: formatCurrency(summary?.revenuePerConversation || 0),
            trend: null,
            trendUp: true,
        },
        {
            icon: Clock,
            label: 'Support Hours Saved',
            value: `${summary?.supportHoursSaved || 0}h`,
            trend: null,
            trendUp: true,
        },
        {
            icon: TrendingDown,
            label: 'Cart Abandonment',
            value: `${summary?.cartAbandonmentRate || 0}%`,
            trend: null,
            trendUp: false,
        },
        {
            icon: Calculator,
            label: 'Cost/Chat',
            value: `$${summary?.costPerConversation || '0.0000'}`,
            trend: null,
            trendUp: true,
        },
        {
            icon: Users,
            label: 'Repeat Users',
            value: `${summary?.repeatRate || 0}%`,
            trend: null,
            trendUp: true,
        },
    ];
    return (_jsxs(motion.div, { style: styles.page, initial: { opacity: 0, y: 8 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.25, ease: 'easeOut' }, children: [_jsxs("div", { style: styles.header, children: [_jsxs("div", { style: styles.headerContent, children: [_jsx("h1", { style: styles.headerTitle, children: t('dashboard') }), _jsx("p", { style: styles.headerSubtitle, children: t('dashboardSubtitle') })] }), _jsxs("div", { style: styles.headerActions, children: [_jsx("div", { style: styles.periodSelector, children: ['day', 'week', 'month', '90days'].map((period) => (_jsx("button", { onClick: () => handlePeriodChange(period), style: selectedPeriod === period ? styles.periodButtonActive : styles.periodButton, disabled: loading, children: t(`period.${period}`) }, period))) }), _jsxs("button", { onClick: handleRefresh, style: styles.refreshButton, disabled: loading, children: [_jsx(RefreshCw, { style: { width: '16px', height: '16px', ...(loading ? styles.spinner : {}) } }), loading ? t('loading') : t('refresh')] })] })] }), _jsx("div", { style: styles.cardsGrid, children: kpiData.map((kpi, index) => {
                    const Icon = kpi.icon;
                    return (_jsxs(motion.div, { style: styles.card, whileHover: { y: -2, boxShadow: '0 8px 25px rgba(0,0,0,0.1)' }, transition: { duration: 0.2 }, children: [_jsx("div", { style: styles.cardIconBox, children: _jsx(Icon, { style: styles.cardIcon }) }), _jsx("div", { style: styles.cardLabel, children: kpi.label || t(kpi.labelKey || '') }), _jsx("div", { style: styles.cardValue, children: loading ? '...' : kpi.value }), _jsx("div", { style: styles.cardTrend, children: kpi.trend !== null ? (_jsxs(_Fragment, { children: [kpi.trendUp ? (_jsx(TrendingUp, { style: { width: '16px', height: '16px', ...styles.trendUp } })) : (_jsx(TrendingDown, { style: { width: '16px', height: '16px', ...styles.trendDown } })), _jsxs("span", { style: kpi.trendUp ? styles.trendUp : styles.trendDown, children: [kpi.trend.toFixed(1), "%"] }), _jsx("span", { style: styles.trendText, children: t('vsLastPeriod') })] })) : (_jsx("span", { style: styles.trendText, children: "\u2014" })) })] }, index));
                }) }), _jsxs("div", { style: styles.chartCard, children: [_jsxs("div", { style: styles.chartHeader, children: [_jsx("h2", { style: styles.chartTitle, children: t('conversationsByHour') }), _jsx("button", { style: styles.chartLink, onClick: () => setShowChartModal(true), children: t('viewDetails') })] }), _jsx(HourlyUsageChart, { data: hourly, height: 260 })] }), _jsxs("div", { style: styles.bottomGrid, children: [_jsxs("div", { style: styles.productsCard, children: [_jsxs("div", { style: styles.productsHeader, children: [_jsx("h2", { style: styles.productsTitle, children: t('topProducts') }), _jsx("button", { style: styles.productsLink, onClick: () => setShowProductsModal(true), children: t('seeAll') })] }), _jsx("div", { children: topProducts.length > 0 ? (topProducts.slice(0, 4).map((product, index) => (_jsxs("div", { style: index === Math.min(topProducts.length, 4) - 1 ? styles.productItemLast : styles.productItem, children: [_jsx("span", { style: styles.productNumber, children: index + 1 }), _jsxs("div", { children: [_jsx("div", { style: styles.productName, children: product.productName }), _jsx("div", { style: styles.productViews, children: t('productStats', { views: product.views, clicks: product.clicks }) })] })] }, product.productId)))) : (_jsxs("div", { style: styles.emptyState, children: [_jsx(Package, { style: { width: '32px', height: '32px', marginBottom: '8px', opacity: 0.5 } }), _jsx("p", { children: t('noProductData') })] })) })] }), _jsxs("div", { style: styles.activityCard, children: [_jsx("h2", { style: styles.activityTitle, children: t('recentActivity') }), _jsx("div", { children: eventsLoading ? (_jsx("div", { style: styles.loadingState, children: _jsx(RefreshCw, { style: { width: '20px', height: '20px', ...styles.spinner } }) })) : recentEvents.length > 0 ? (recentEvents.slice(0, 5).map((event, index) => {
                                    const { icon: Icon, color, bg } = getActivityIcon(event.event_type);
                                    const { title, subtitle } = getActivityTitle(event);
                                    return (_jsxs("div", { style: styles.activityItem, children: [_jsx("div", { style: { ...styles.activityIconBox, backgroundColor: bg }, children: _jsx(Icon, { style: { width: '16px', height: '16px', color } }) }), _jsxs("div", { style: styles.activityContent, children: [_jsx("div", { style: styles.activityItemTitle, children: title }), subtitle && (_jsx("div", { style: styles.activityItemSubtitle, children: subtitle })), _jsx("div", { style: styles.activityItemTime, children: formatTimeAgo(event.created_at) })] })] }, index));
                                })) : (_jsxs("div", { style: styles.emptyState, children: [_jsx(User, { style: { width: '32px', height: '32px', marginBottom: '8px', opacity: 0.5 } }), _jsx("p", { children: t('noRecentActivity') })] })) })] })] }), _jsxs(Modal, { isOpen: showChartModal, onClose: () => setShowChartModal(false), title: t('conversationsByHourDetailed'), children: [_jsxs("div", { style: { marginBottom: '24px' }, children: [_jsx("p", { style: { color: '#6b7280', marginBottom: '16px' }, children: t('chartModalDescription') }), hourly.length > 0 ? (_jsx("div", { style: { display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '12px' }, children: hourly.map((item, idx) => (_jsxs("div", { style: {
                                        padding: '16px',
                                        backgroundColor: '#f9fafb',
                                        borderRadius: '12px',
                                        textAlign: 'center',
                                    }, children: [_jsx("div", { style: { fontSize: '24px', fontWeight: 700, color: '#6366f1' }, children: item.count }), _jsx("div", { style: { fontSize: '13px', color: '#6b7280', marginTop: '4px' }, children: item.hour })] }, idx))) })) : (_jsxs("div", { style: styles.emptyState, children: [_jsx(Clock, { style: { width: '32px', height: '32px', marginBottom: '8px', opacity: 0.5 } }), _jsx("p", { children: t('noHourlyData') })] }))] }), hourly.length > 0 && (_jsxs("div", { style: { marginTop: '24px' }, children: [_jsx("h3", { style: { fontSize: '16px', fontWeight: 600, color: '#111827', marginBottom: '12px' }, children: t('summary') }), _jsxs("div", { style: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }, children: [_jsxs("div", { style: { padding: '16px', backgroundColor: '#eff6ff', borderRadius: '12px' }, children: [_jsx("div", { style: { fontSize: '14px', color: '#6b7280' }, children: t('peakHour') }), _jsx("div", { style: { fontSize: '20px', fontWeight: 600, color: '#111827' }, children: hourly.reduce((max, h) => h.count > max.count ? h : max, hourly[0])?.hour || 'N/A' })] }), _jsxs("div", { style: { padding: '16px', backgroundColor: '#ecfdf5', borderRadius: '12px' }, children: [_jsx("div", { style: { fontSize: '14px', color: '#6b7280' }, children: t('totalConversations') }), _jsx("div", { style: { fontSize: '20px', fontWeight: 600, color: '#111827' }, children: hourly.reduce((sum, h) => sum + h.count, 0) })] }), _jsxs("div", { style: { padding: '16px', backgroundColor: '#fef3c7', borderRadius: '12px' }, children: [_jsx("div", { style: { fontSize: '14px', color: '#6b7280' }, children: t('averagePerHour') }), _jsx("div", { style: { fontSize: '20px', fontWeight: 600, color: '#111827' }, children: (hourly.reduce((sum, h) => sum + h.count, 0) / (hourly.length || 1)).toFixed(1) })] })] })] }))] }), _jsx(Modal, { isOpen: showProductsModal, onClose: () => setShowProductsModal(false), title: t('allTopProducts'), children: _jsx("div", { children: topProducts.length > 0 ? (_jsx("div", { style: { display: 'flex', flexDirection: 'column', gap: '12px' }, children: topProducts.map((product, index) => (_jsxs("div", { style: {
                                display: 'flex',
                                alignItems: 'center',
                                gap: '16px',
                                padding: '16px',
                                backgroundColor: '#f9fafb',
                                borderRadius: '12px',
                            }, children: [_jsx("span", { style: {
                                        fontSize: '18px',
                                        fontWeight: 700,
                                        color: index < 3 ? '#6366f1' : '#d1d5db',
                                        width: '32px',
                                        textAlign: 'center',
                                    }, children: index + 1 }), _jsxs("div", { style: { flex: 1 }, children: [_jsx("div", { style: { fontSize: '15px', fontWeight: 500, color: '#111827' }, children: product.productName }), _jsx("div", { style: { fontSize: '14px', color: '#6b7280', marginTop: '4px' }, children: t('productModalStats', { views: product.views, clicks: product.clicks, conversion: formatPercent(product.conversionRate) }) })] }), _jsxs("div", { style: { textAlign: 'right' }, children: [_jsx("div", { style: { fontSize: '18px', fontWeight: 600, color: '#10b981' }, children: product.views }), _jsx("div", { style: { fontSize: '12px', color: '#6b7280' }, children: t('views') })] })] }, product.productId))) })) : (_jsxs("div", { style: styles.emptyState, children: [_jsx(Package, { style: { width: '32px', height: '32px', marginBottom: '8px', opacity: 0.5 } }), _jsx("p", { children: t('noProductData') })] })) }) })] }));
}
export default Dashboard;
